import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import PageHeader from '../../components/PageHeader';
import { PackageCheck, CheckCircle2, ArrowRight, ChevronRight, ScanLine, UserRound, Hand, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import { parseLotScan, scanMatches } from '../../utils/lotScan';
import { rowLabel } from '../../utils/nurseryRowSlots';
import { pickCustomers, customerDone, heldByOther, pickGroups, freeLineGroups, type PickCustomer, type PickGroup, type FreeLineGroup } from '../../utils/pickCustomers';
import { ApiError } from '../../types/api';
import nurseryService from '../../services/nursery';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { PickItemCard } from '../../components/nursery/picking/PickItemCard';
import { FreePickLineCard } from '../../components/nursery/picking/FreePickLineCard';
import { CombinedPickCard } from '../../components/nursery/picking/CombinedPickCard';
import { CombinedFreeLineCard } from '../../components/nursery/picking/CombinedFreeLineCard';
import colourService from '../../services/nurseryColour';
import type { LotBand } from '../../types/nurseryColour';
import { LOAD_TYPE_LABELS, type FreePickLine, type PickItem, type PickList } from '../../types/nursery';
import type { PickQuarantineOrder } from '../../types/nurseryQuarantine';
import { QuarantinePickNotice } from '../../components/nursery/picking/QuarantinePickNotice';

/**
 * /nursery/picking/:id — one load's pick list, one customer at a time.
 *
 * A load carries several customers' orders. The picker first chooses a
 * customer (`?customer=`), then sees only that customer's trees — every order
 * of theirs on the load combined, the same lot on several orders as one card
 * (picks are still recorded per order) — in walk order, so their trees go on the
 * trolley together and the picker goes down each row once. Anything not at
 * the despatch farm sinks to the bottom, flagged. A load with one customer
 * opens straight on them.
 *
 * Several pickers can work one load, never the same customer. Opening a
 * customer claims them, and the page keeps the claim alive while it is open.
 * A customer someone else holds says who; opening them anyway shows that and
 * offers Take over, for the picker who walked off. The server enforces it:
 * a pick for a customer someone else holds is refused.
 *
 * A load picked without allocating lists order lines instead of allocations:
 * each with every place its tree stands at the despatch farm, in walk order,
 * and the picker chooses (FreePickLineCard). Lines are grouped under the row
 * of their first place, so the picker still walks each row once.
 */

/** How often an open customer's claim is renewed. The server lets one lapse after 20 minutes. */
const CLAIM_HEARTBEAT_MS = 2 * 60_000;
/** How often the customer list refreshes, so a picker sees who has taken whom. */
const CHOOSER_REFRESH_MS = 20_000;
/** Remembered per browser: whether picked-in-full cards stay on the list. */
const SHOW_PICKED_KEY = 'nursery_pick_show_picked';

/**
 * Picked in full: nothing left to do with it, so it leaves the list unless
 * the picker asks to see it. A short pick stays, since it may still take
 * trees from another lot.
 */
const pickedInFull = (g: PickGroup) => g.pickedInFull;

function readShowPicked(): boolean {
  try { return localStorage.getItem(SHOW_PICKED_KEY) === '1'; } catch { return false; }
}

interface Section {
  key: string;
  title: string;
  groups: PickGroup[];
}

const linePickedInFull = (g: FreeLineGroup) => g.lines.every((ln) => ln.status === 'picked');

interface LineSection {
  key: string;
  title: string;
  groups: FreeLineGroup[];
}

/**
 * A free load's lines, already in walk order from the server, in runs that
 * start in the same row: each line is headed by its first place with trees
 * to take (or already taken). Lines with nowhere to take from go last.
 */
function lineSectionsFor(groups: FreeLineGroup[], t: TFunction): LineSection[] {
  const out: LineSection[] = [];
  const nowhere: FreeLineGroup[] = [];
  for (const g of groups) {
    const first = g.lines[0].lots.find((l) => l.free > 0 || g.lines.some((ln) => ln.lots.some((x) => x.lot_id === l.lot_id && x.pick)));
    if (!first) { nowhere.push(g); continue; }
    const key = `loc:${first.block_name ?? ''}:${first.row_number ?? first.location_name ?? ''}:${first.row_side ?? ''}`;
    const title = [first.block_name, first.row_number != null ? rowLabel(first.row_number, first.row_side) : null].filter(Boolean).join(' · ')
      || first.location_name || t('noLocation');
    const last = out[out.length - 1];
    if (last && last.key === key) last.groups.push(g);
    else out.push({ key, title: t('startingAt', { place: title }), groups: [g] });
  }
  if (nowhere.length) out.push({ key: 'nowhere', title: t('noStockFree'), groups: nowhere });
  return out;
}

/** Consecutive runs of a walk-ordered list that stand in the same row. */
function sectionsFor(groups: PickGroup[], t: TFunction): Section[] {
  const out: Section[] = [];
  for (const g of groups) {
    const it = g.items[0];
    let key: string;
    let title: string;
    if (!it.at_despatch_farm) {
      key = 'elsewhere';
      title = t('notAtFarmYet');
    } else {
      key = `loc:${it.block_name ?? ''}:${it.row_number ?? it.location_name ?? ''}:${it.row_side ?? ''}`;
      title = [it.block_name, it.row_number != null ? rowLabel(it.row_number, it.row_side) : null].filter(Boolean).join(' · ') || it.location_name || t('noLocation');
    }
    const last = out[out.length - 1];
    if (last && last.key === key) last.groups.push(g);
    else out.push({ key, title, groups: [g] });
  }
  return out;
}

function ProgressBar({ done, of }: { done: number; of: number }) {
  const pct = of ? Math.round((done / of) * 100) : 0;
  return (
    <div className="h-2.5 rounded-full bg-neutral-100 overflow-hidden">
      <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** The customer's orders going under a quarantine protocol: they are taken to be treated once picked. */
function quarantineFor(c: PickCustomer, list: PickList): PickQuarantineOrder[] {
  const ids = new Set<number>([...c.items.map((i) => i.order_id), ...c.lines.map((l) => l.order_id)]);
  return (list.quarantine ?? []).filter((q) => ids.has(q.order_id));
}

function customerLine(c: PickCustomer, tz: string, t: TFunction): string {
  return [
    c.code ? t('store', { code: c.code }) : null,
    c.order_numbers.join(', '),
    c.collection_at ? t('collecting', { when: formatDateTimeInTz(c.collection_at, tz) }) : null,
  ].filter(Boolean).join(' · ');
}

function PickListPage() {
  const { t } = useTranslation('nursery', { keyPrefix: 'PickListPage' });
  const { id } = useParams<{ id: string }>();
  const loadId = Number(id);
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const customerParam = params.get('customer');
  const [list, setList] = useState<PickList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<number | null>(null);
  // On a free load, the line a scanned lot was found on.
  const [foundLine, setFoundLine] = useState<number | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);
  // Someone else holds the customer this page is open on.
  const [blocked, setBlocked] = useState<{ key: string; message: string } | null>(null);
  const [takingOver, setTakingOver] = useState(false);
  const [showPicked, setShowPickedState] = useState(readShowPicked);
  const setShowPicked = (v: boolean) => {
    setShowPickedState(v);
    try { localStorage.setItem(SHOW_PICKED_KEY, v ? '1' : '0'); } catch { /* per-browser convenience only */ }
  };

  const fetchList = useCallback(async () => {
    try {
      const res = await nurseryService.getPickList(loadId);
      if (res.success) setList(res.data);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [loadId, t]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // The paint bands on the lots, so a picker can tell lines apart in the row.
  // Only when the nursery colour-codes; a failure shows no colours, nothing else.
  const [bands, setBands] = useState<Record<string, LotBand[]>>({});
  const lotIdsKey = useMemo(() => {
    if (!list) return '';
    const ids = new Set<number>();
    for (const i of list.items) if (i.lot_id) ids.add(i.lot_id);
    for (const ln of list.lines ?? []) for (const l of ln.lots) ids.add(l.lot_id);
    return [...ids].sort((a, b) => a - b).slice(0, 500).join(',');
  }, [list]);
  useEffect(() => {
    if (!lotIdsKey) return;
    let live = true;
    colourService.bands(lotIdsKey.split(',').map(Number))
      .then((res) => { if (live && res.success && res.data.enabled) setBands(res.data.lots); })
      .catch(() => { /* no colours */ });
    return () => { live = false; };
  }, [lotIdsKey]);

  const customers = useMemo(
    () => (list ? pickCustomers(list.items, list.load.load_type === 'collection', list.claims ?? [], list.lines ?? []) : []),
    [list],
  );
  const current = customers.length === 1
    ? customers[0]
    : customers.find((c) => c.key === customerParam) ?? null;
  const currentKey = current?.key ?? null;
  const free = list?.mode === 'free';
  const groups = useMemo(() => (current ? pickGroups(current.items) : []), [current]);
  const lineGroups = useMemo(() => (current ? freeLineGroups(current.lines) : []), [current]);
  const pickedCount = groups.filter(pickedInFull).length + lineGroups.filter(linePickedInFull).length;
  const sections = useMemo(
    () => sectionsFor(showPicked ? groups : groups.filter((g) => !pickedInFull(g)), t),
    [groups, showPicked, t],
  );
  const lineSections = useMemo(
    () => lineSectionsFor(showPicked ? lineGroups : lineGroups.filter((g) => !linePickedInFull(g)), t),
    [lineGroups, showPicked, t],
  );
  const isBlocked = !!blocked && blocked.key === currentKey;

  /**
   * Hold the customer this page is open on: claim on opening, renew every
   * couple of minutes, let go on leaving. Refused because someone else holds
   * them, the page says who and offers Take over; renewal keeps trying, so a
   * hold that lapses frees the page by itself. Any other failure (an older
   * server with no claims, no signal) is let pass: picks are checked anyway.
   */
  useEffect(() => {
    if (!currentKey) return;
    let stopped = false;
    const claim = async () => {
      try {
        const res = await nurseryService.claimPickCustomer(loadId, currentKey);
        if (stopped) return;
        if (res.success && res.data) setList(res.data);
        setBlocked((b) => (b?.key === currentKey ? null : b));
      } catch (e) {
        if (!stopped && e instanceof ApiError && e.code === 'CUSTOMER_CLAIMED') {
          setBlocked({ key: currentKey, message: e.message });
        }
      }
    };
    claim();
    const timer = setInterval(claim, CLAIM_HEARTBEAT_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
      // Only deletes a hold that is ours, so leaving a customer someone else holds is harmless.
      nurseryService.releasePickCustomer(loadId, currentKey).catch(() => undefined);
    };
  }, [loadId, currentKey]);

  // On the customer list, keep who-is-picking-whom current.
  useEffect(() => {
    if (currentKey) return;
    const timer = setInterval(fetchList, CHOOSER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [currentKey, fetchList]);

  const takeOver = async () => {
    if (!currentKey) return;
    setTakingOver(true);
    try {
      const res = await nurseryService.claimPickCustomer(loadId, currentKey, true);
      if (res.success && res.data) setList(res.data);
      setBlocked(null);
    } catch (e) {
      setBlocked({ key: currentKey, message: errorMessage(e, t('takeOverFailed')) });
    } finally {
      setTakingOver(false);
    }
  };

  const chooseCustomer = (key: string | null) => {
    setFound(null);
    setFoundLine(null);
    setScanNote(null);
    setParams(key ? { customer: key } : {});
    window.scrollTo({ top: 0 });
  };

  /**
   * A scanned lot label finds that lot on this customer's list: the first
   * item still to pick from it is highlighted and scrolled to. A lot that is
   * on the load for someone else says who, rather than leaving the picker
   * wondering whether the scan worked.
   */
  const onScan = (raw: string) => {
    setScanning(false);
    const scan = parseLotScan(raw);
    if (!scan || !current) return;
    const lotName = 'lotId' in scan ? t('lotNumber', { id: scan.lotId }) : scan.code;
    if (free) {
      // Picked without allocating: the lines this lot can fill, the first still open.
      const lotOf = (ln: FreePickLine) => ln.lots.find((l) => scanMatches(scan, { id: l.lot_id, code: l.lot_code }));
      const hits = current.lines.filter((ln) => lotOf(ln));
      if (!hits.length) {
        setFound(null);
        setFoundLine(null);
        setScanNote(t('scanNotAPlace', { lot: lotName, name: current.name }));
        return;
      }
      const target = hits.find((ln) => !ln.resolved) ?? hits[0];
      const group = lineGroups.find((g) => g.lines.includes(target));
      if (target.status === 'picked') setShowPicked(true);
      setFound(lotOf(target)!.lot_id);
      setFoundLine(target.order_line_id);
      setScanNote(t('scanLines', { count: hits.length, name: current.name }));
      const elementId = group && group.lines.length > 1 ? `free-group-${group.key}` : `free-${target.order_line_id}`;
      requestAnimationFrame(() => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      return;
    }
    const isLot = (i: PickItem) => scanMatches(scan, { id: i.lot_id, code: i.lot_code });
    const matches = current.items.filter(isLot);
    if (!matches.length) {
      setFound(null);
      const others = customers.filter((c) => c !== current && c.items.some(isLot)).map((c) => c.name);
      setScanNote(others.length
        ? t('scanForOthers', { lot: lotName, others: others.join(', '), name: current.name })
        : t('scanNotOnLoad', { lot: lotName }));
      return;
    }
    const target = matches.find((i) => !i.pick) ?? matches[0];
    const group = groups.find((g) => g.items.includes(target));
    // A scan of a lot already picked in full has to show its card.
    if (group && pickedInFull(group)) setShowPicked(true);
    setFound(target.lot_id);
    setScanNote(t('scanItems', { count: matches.length, name: current.name }));
    const elementId = group && group.items.length > 1 ? `pick-group-${group.key}` : `pick-${target.allocation_id}`;
    requestAnimationFrame(() => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  if (!list) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        {error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <Link to="/nursery/picking" className="text-sm text-primary-700 underline">{t('backToPicking')}</Link>
          </div>
        ) : <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />}
      </div>
    );
  }

  const { load, progress } = list;
  const editable = ['released', 'picking', 'loaded'].includes(load.status);
  const loadDone = progress.items > 0 && progress.resolved >= progress.items;
  const several = customers.length > 1;
  const nextCustomer = current ? customers.find((c) => c !== current && !customerDone(c) && !heldByOther(c)) : undefined;
  const loadNotes = (load.vehicle || load.driver_name || load.notes) ? (
    <p className="text-xs text-neutral-600 mt-2 break-words whitespace-pre-line">
      {[load.vehicle, load.driver_name].filter(Boolean).join(' · ')}
      {load.notes ? `${load.vehicle || load.driver_name ? '\n' : ''}${load.notes}` : ''}
    </p>
  ) : null;

  return (
    <>
      <PageHeader
        title={t(`loadType.${load.load_type}`, { defaultValue: LOAD_TYPE_LABELS[load.load_type] })}
        subtitle={`${load.load_number} · ${formatLocalDateInTz(load.despatch_date, tz)} · ${load.despatch_farm_name}`}
        icon={<PackageCheck className="h-6 w-6 text-white" />}
        // Back goes up one level, not back in history: a customer opened from
        // a link or a notification still returns to this load's customers.
        onBack={() => (current && several ? chooseCustomer(null) : navigate('/nursery/picking'))}
      />
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {!current ? (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-neutral-800">
                  {t(free ? 'progressLines' : 'progressItems', { done: progress.resolved, total: progress.items, picked: progress.picked_trees.toLocaleString(), trees: progress.trees.toLocaleString() })}
                </span>
                <LoadStatusBadge status={load.status} />
              </div>
              <div className="mt-2"><ProgressBar done={progress.resolved} of={progress.items} /></div>
              {free && (
                <p className="text-xs text-neutral-600 mt-2 break-words">
                  {t('freeNote', { farm: load.despatch_farm_name })}
                </p>
              )}
              {loadNotes}
            </div>

            {loadDone && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>{t('loadDone')}</span>
              </div>
            )}

            {customers.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">
                {free ? t('noTreesToPick') : t('nothingAllocated')}
              </p>
            ) : (
              <section aria-label={t('customersOnLoad')}>
                <h2 className="text-sm font-semibold text-neutral-700">{t('chooseCustomer')}</h2>
                <ul className="mt-2 space-y-3">
                  {customers.map((c) => {
                    const done = customerDone(c);
                    const codes = [...new Set(quarantineFor(c, list).map((q) => q.code))];
                    return (
                      <li key={c.key}>
                        <button
                          type="button"
                          onClick={() => chooseCustomer(c.key)}
                          className={`w-full text-left border rounded-xl p-4 flex items-center gap-3 hover:border-primary-400 ${done ? 'bg-white border-green-200' : heldByOther(c) ? 'bg-neutral-50 border-neutral-200' : 'bg-white border-neutral-200'}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              {done && <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" aria-label={t('allPicked')} />}
                              <span className="font-medium text-neutral-900 break-words min-w-0">{c.name}</span>
                            </div>
                            <p className="text-xs text-neutral-500 break-words mt-0.5">{customerLine(c, tz, t)}</p>
                            {codes.length > 0 && (
                              <p className="inline-flex items-center gap-1 mt-1.5 mr-1.5 text-xs font-medium rounded-full px-2 py-0.5 max-w-full bg-amber-100 text-amber-900">
                                <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="break-words min-w-0">{t('quarantineChip', { codes: codes.join(', ') })}</span>
                              </p>
                            )}
                            {c.claim && !done && (
                              <p className={`inline-flex items-center gap-1 mt-1.5 text-xs font-medium rounded-full px-2 py-0.5 max-w-full ${c.claim.mine ? 'bg-primary-50 text-primary-800' : 'bg-amber-50 text-amber-800'}`}>
                                <UserRound className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="break-words min-w-0">{c.claim.mine ? t('youArePicking') : t('isPicking', { name: c.claim.user_name || t('someoneElse') })}</span>
                              </p>
                            )}
                            <p className="text-xs text-neutral-700 mt-2">
                              {t(free ? 'progressLines' : 'progressItems', { done: c.resolved, total: c.units, picked: c.picked_trees.toLocaleString(), trees: c.trees.toLocaleString() })}
                            </p>
                            <div className="mt-1"><ProgressBar done={c.resolved} of={c.units} /></div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </>
        ) : (
          <>
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 break-words">{current.name}</p>
                  <p className="text-xs text-neutral-500 break-words">{customerLine(current, tz, t)}</p>
                </div>
                <LoadStatusBadge status={load.status} />
              </div>
              <p className="text-sm font-medium text-neutral-800 mt-2">
                {t(free ? 'progressLines' : 'progressItems', { done: current.resolved, total: current.units, picked: current.picked_trees.toLocaleString(), trees: current.trees.toLocaleString() })}
              </p>
              <div className="mt-2"><ProgressBar done={current.resolved} of={current.units} /></div>
              {loadNotes}
            </div>

            {isBlocked ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-3">
                <div className="flex items-start gap-2">
                  <Hand className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words min-w-0">{blocked?.message}</span>
                </div>
                <p className="text-xs break-words">
                  {t('takeOverNote', { name: current.name })}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={takeOver} disabled={takingOver} className="max-w-full">
                    <span className="truncate">{takingOver ? t('takingOver') : t('takeOver')}</span>
                  </Button>
                  {several && (
                    <Button variant="outline" size="sm" onClick={() => chooseCustomer(null)}>{t('chooseAnother')}</Button>
                  )}
                </div>
              </div>
            ) : (<>
            {/* An order going interstate under a quarantine protocol is taken to its treatment area once picked. */}
            <QuarantinePickNotice
              orders={quarantineFor(current, list)}
              seenKey={`${load.id}:${current.key}`}
              done={customerDone(current)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setScanNote(null); setScanning(true); }}>
                <ScanLine className="h-4 w-4" /> {t('scanLabel')}
              </Button>
              {pickedCount > 0 && (
                <Button variant="outline" size="sm" onClick={() => setShowPicked(!showPicked)} aria-pressed={showPicked}>
                  {showPicked
                    ? <><EyeOff className="h-4 w-4" /> {t('hidePicked')}</>
                    : <><Eye className="h-4 w-4" /> {t('showPicked', { n: pickedCount })}</>}
                </Button>
              )}
              {scanNote && <span className="text-sm text-neutral-700 break-words min-w-0">{scanNote}</span>}
            </div>

            {customerDone(current) && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words min-w-0">
                    {several
                      ? t('customerDone', { name: current.name })
                      : t('loadDone')}
                  </span>
                </div>
                {nextCustomer && (
                  <Button size="sm" onClick={() => chooseCustomer(nextCustomer.key)} className="max-w-full">
                    <span className="truncate">{t('next', { name: nextCustomer.name })}</span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0" />
                  </Button>
                )}
                {several && !nextCustomer && loadDone && (
                  <p>{t('lastCustomer')}</p>
                )}
              </div>
            )}

            {sections.map((s) => (
              <section key={`${s.key}:${s.groups[0].items[0].allocation_id}`} aria-label={s.title}>
                <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
                <ul className="mt-2 space-y-3">
                  {s.groups.map((g) => g.items.length > 1 ? (
                    <CombinedPickCard
                      key={g.key}
                      group={g}
                      loadId={load.id}
                      despatchFarmName={load.despatch_farm_name}
                      timezone={tz}
                      editable={editable}
                      onChanged={setList}
                      highlighted={found === g.items[0].lot_id}
                      bands={bands[g.items[0].lot_id]}
                    />
                  ) : (
                    <PickItemCard
                      key={g.items[0].allocation_id}
                      item={g.items[0]}
                      loadId={load.id}
                      despatchFarmName={load.despatch_farm_name}
                      timezone={tz}
                      editable={editable}
                      onChanged={setList}
                      highlighted={found === g.items[0].lot_id}
                      bands={g.items[0].lot_id ? bands[g.items[0].lot_id] : undefined}
                    />
                  ))}
                </ul>
              </section>
            ))}
            {lineSections.map((s) => (
              <section key={`${s.key}:${s.groups[0].lines[0].order_line_id}`} aria-label={s.title}>
                <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
                <ul className="mt-2 space-y-3">
                  {s.groups.map((g) => g.lines.length > 1 ? (
                    <CombinedFreeLineCard
                      key={g.key}
                      group={g}
                      loadId={load.id}
                      despatchFarmName={load.despatch_farm_name}
                      editable={editable}
                      onChanged={setList}
                      highlightedLotId={g.lines.some((l) => l.order_line_id === foundLine) ? found : null}
                      bandsByLot={bands}
                    />
                  ) : (
                    <FreePickLineCard
                      key={g.lines[0].order_line_id}
                      line={g.lines[0]}
                      loadId={load.id}
                      despatchFarmName={load.despatch_farm_name}
                      editable={editable}
                      onChanged={setList}
                      highlightedLotId={foundLine === g.lines[0].order_line_id ? found : null}
                      bandsByLot={bands}
                    />
                  ))}
                </ul>
              </section>
            ))}
            </>)}
          </>
        )}
      </div>
      {scanning && <BarcodeScanner label={t('scanLabel')} onScan={onScan} onClose={() => setScanning(false)} />}
    </>
  );
}

export default PickListPage;
